import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api, errorText } from '../lib/api';
import { useMe } from '../lib/session';
import { fmtTime } from '../lib/format';
import { Button, Card, CardHeader, cx, Empty, ErrorText, Field, Input, PageHeader, Pill, Segmented, Stat } from '../components/ui';

/** INT-DSH-03: Master Trader compliance board — who is keeping up, for a chosen period, worst first. */

type Period = 'this_week' | 'last_week' | 'this_month' | 'custom';
type Metric = 'analyses' | 'progress' | 'closing' | 'stale_setups' | 'unplanned' | 'learning';
type Outcome = 'on_time' | 'late' | 'missed' | 'open';
interface Counts {
  onTime: number;
  late: number;
  missed: number;
  open: number;
}
interface Row {
  trader: { id: string; name: string; status: string };
  rate: number | null;
  analyses: Counts;
  progress: Counts;
  closing: Counts;
  staleSetups: number;
  unplannedTrades: number;
  learning: { required: number; completed: number; overdue: number } | null;
  missed: number;
  issues: number;
}
interface Board {
  period: { from: string; to: string; fromDay: string; toDay: string; label: string; kind: Period; timezone: string };
  rows: Row[];
  totals: { analyses: Counts; progress: Counts; closing: Counts; staleSetups: number; unplannedTrades: number; rate: number | null };
}
interface Items {
  trader: { id: string; name: string };
  items: { id: string; title: string; detail: string; at: string; completedAt: string | null; outcome: Outcome | null; status: string | null; link: string | null }[];
}
interface Drill {
  trader: Row['trader'];
  metric: Metric;
  outcome?: Outcome;
}

const PERIODS: { value: Period; label: string }[] = [
  { value: 'this_week', label: 'This week' },
  { value: 'last_week', label: 'Last week' },
  { value: 'this_month', label: 'This month' },
  { value: 'custom', label: 'Custom' },
];
const METRIC_LABEL: Record<Metric, string> = { analyses: 'Daily analyses', progress: 'Progress reports', closing: 'Closing reports', stale_setups: 'Stale setups', unplanned: 'Unplanned trades', learning: 'Required learning' };
const OUTCOME_LABEL: Record<Outcome, string> = { on_time: 'on time', late: 'late', missed: 'missed', open: 'still open' };
const TONE: Record<Outcome, 'up' | 'warn' | 'down' | 'neutral'> = { on_time: 'up', late: 'warn', missed: 'down', open: 'neutral' };

const rateTone = (r: number | null) => (r === null ? 'text-muted' : r >= 90 ? 'text-up' : r >= 70 ? 'text-warn' : 'text-down');

/** One clickable number. Zero is quiet; problems are coloured. */
function Num({ n, tone, label, onOpen, active }: { n: number; tone: 'up' | 'warn' | 'down' | 'neutral'; label: string; onOpen: () => void; active: boolean }) {
  const color = n === 0 ? 'text-muted' : { up: 'text-up', warn: 'text-warn', down: 'text-down', neutral: 'text-ink2' }[tone];
  const bg = n > 0 && tone === 'down' ? 'bg-down-bg' : n > 0 && tone === 'warn' ? 'bg-warn-bg' : 'bg-transparent';
  return (
    <button type="button" aria-label={label} title={label} onClick={onOpen} disabled={n === 0} className={cx('min-h-8 min-w-8 cursor-pointer rounded-md px-1.5 font-mono text-[15px] font-semibold disabled:cursor-default', color, bg, active && 'ring-2 ring-accent')}>
      {n}
    </button>
  );
}

function Triple({ c, who, what, metric, open, drill, onlyMissed }: { c: Counts; who: Row['trader']; what: string; metric: Metric; open: (d: Drill) => void; drill: Drill | null; onlyMissed?: boolean }) {
  const is = (o: Outcome) => drill?.trader.id === who.id && drill.metric === metric && drill.outcome === o;
  const parts: Outcome[] = onlyMissed ? ['missed'] : ['on_time', 'late', 'missed'];
  return (
    <div className="flex items-center gap-0.5">
      {parts.map((o, i) => (
        <span key={o} className="flex items-center">
          {i > 0 && <span className="px-0.5 text-line2" aria-hidden="true">·</span>}
          <Num n={c[o === 'on_time' ? 'onTime' : o]} tone={TONE[o]} label={`${who.name}: ${c[o === 'on_time' ? 'onTime' : o]} ${what} ${OUTCOME_LABEL[o]}`} onOpen={() => open({ trader: who, metric, outcome: o })} active={is(o)} />
        </span>
      ))}
      {!onlyMissed && c.open > 0 && <span className="ml-1 text-xs text-muted">+{c.open} open</span>}
    </div>
  );
}

export function Compliance() {
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const [period, setPeriod] = useState<Period>('this_week');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [applied, setApplied] = useState({ from: '', to: '' });
  const [drill, setDrill] = useState<Drill | null>(null);
  const drillRef = useRef<HTMLDivElement>(null);

  const qs = period === 'custom' ? `period=custom&from=${applied.from}&to=${applied.to}` : `period=${period}`;
  const ready = period !== 'custom' || (!!applied.from && !!applied.to);
  const board = useQuery({ queryKey: ['compliance', qs], queryFn: () => api<Board>(`/dashboards/compliance?${qs}`), enabled: ready, refetchInterval: 60_000 });
  const items = useQuery({
    queryKey: ['compliance-items', qs, drill?.trader.id, drill?.metric, drill?.outcome],
    queryFn: () => api<Items>(`/dashboards/compliance/items?${qs}&traderId=${drill!.trader.id}&metric=${drill!.metric}${drill!.outcome ? `&outcome=${drill!.outcome}` : ''}`),
    enabled: ready && !!drill,
  });
  useEffect(() => {
    if (drill) drillRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [drill]);
  useEffect(() => setDrill(null), [qs]);

  const b = board.data;
  const t = b?.totals;
  const open = (d: Drill) => setDrill(d);

  return (
    <>
      <PageHeader title="Compliance" sub={b ? `${b.period.label} · times in ${tz}` : 'Who is keeping up with analyses, reports and setups'} />

      <Card className="flex flex-wrap items-end gap-3 p-3.5">
        <Segmented label="Period" options={PERIODS} value={period} onChange={setPeriod} />
        {period === 'custom' && (
          <div className="flex flex-wrap items-end gap-2">
            <Field label="From">
              <Input type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} />
            </Field>
            <Field label="To">
              <Input type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} />
            </Field>
            <Button variant="primary" onClick={() => setApplied(custom)} disabled={!custom.from || !custom.to}>
              Show
            </Button>
          </div>
        )}
        <span className="ml-auto flex flex-wrap items-center gap-3 text-xs text-muted">
          <span><span className="text-up">●</span> on time</span>
          <span><span className="text-warn">●</span> late</span>
          <span><span className="text-down">●</span> missed</span>
          <span>Click a number to see the items</span>
        </span>
      </Card>
      <ErrorText error={board.error ? errorText(board.error) : null} />

      {t && (
        <section aria-label="Floor totals" className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Stat label="Floor on time" value={t.rate === null ? '—' : `${t.rate} %`} tone={rateTone(t.rate)} note="analyses and reports finished on time" />
          <Stat label="Analyses missed" value={t.analyses.missed} tone={t.analyses.missed ? 'text-down' : ''} note={`${t.analyses.late} late · ${t.analyses.onTime} on time`} />
          <Stat label="Reports missed" value={t.progress.missed + t.closing.missed} tone={t.progress.missed + t.closing.missed ? 'text-down' : ''} note={`${t.progress.late} progress reports late`} />
          <Stat label="Stale setups" value={t.staleSetups} tone={t.staleSetups ? 'text-warn' : ''} note="a setup update was missed" />
          <Stat label="Unplanned trades" value={t.unplannedTrades} tone={t.unplannedTrades ? 'text-warn' : ''} note="logged without an approved plan" />
        </section>
      )}

      <Card>
        <CardHeader title={<>Traders <span className="font-normal text-muted">· worst first</span></>} right={b && <span className="text-xs text-muted">{b.rows.length} people</span>} />
        <div className="overflow-x-auto">
          <table className="w-full min-w-[880px] border-collapse">
            <thead>
              <tr className="text-left text-xs text-muted">
                <th className="sticky left-0 z-[1] bg-panel px-4 py-2.5 font-medium">Trader</th>
                <th className="px-3 py-2.5 text-right font-medium">On time</th>
                <th className="px-3 py-2.5 font-medium">
                  Analyses
                  <br />
                  <span className="font-normal">on time · late · missed</span>
                </th>
                <th className="px-3 py-2.5 font-medium">
                  Progress reports
                  <br />
                  <span className="font-normal">on time · late · missed</span>
                </th>
                <th className="px-3 py-2.5 font-medium">
                  Closing
                  <br />
                  <span className="font-normal">missed</span>
                </th>
                <th className="px-3 py-2.5 font-medium">Stale setups</th>
                <th className="px-3 py-2.5 font-medium">Unplanned</th>
                <th className="px-3 py-2.5 font-medium">Required learning</th>
              </tr>
            </thead>
            <tbody>
              {b?.rows.map((r) => {
                const flag = r.missed > 0 ? 'border-l-down' : r.issues > 0 ? 'border-l-warn' : 'border-l-transparent';
                return (
                  <tr key={r.trader.id} className={cx('border-t border-[#1a1f27] border-l-[3px]', flag)}>
                    <td className="sticky left-0 z-[1] bg-panel px-4 py-2.5 shadow-[6px_0_8px_-6px_rgba(0,0,0,0.6)] sm:shadow-none">
                      <span className="font-semibold">{r.trader.name}</span>
                      {r.trader.status !== 'ACTIVE' && (
                        <Pill tone="neutral" className="ml-2">
                          {r.trader.status.toLowerCase()}
                        </Pill>
                      )}
                    </td>
                    <td className={cx('px-3 py-2.5 text-right font-mono text-[15px] font-semibold', rateTone(r.rate))}>{r.rate === null ? '—' : `${r.rate} %`}</td>
                    <td className="px-3 py-2.5">
                      <Triple c={r.analyses} who={r.trader} what="analyses" metric="analyses" open={open} drill={drill} />
                    </td>
                    <td className="px-3 py-2.5">
                      <Triple c={r.progress} who={r.trader} what="progress reports" metric="progress" open={open} drill={drill} />
                    </td>
                    <td className="px-3 py-2.5">
                      <Triple c={r.closing} who={r.trader} what="closing reports" metric="closing" open={open} drill={drill} onlyMissed />
                    </td>
                    <td className="px-3 py-2.5">
                      <Num n={r.staleSetups} tone="warn" label={`${r.trader.name}: ${r.staleSetups} stale setups`} onOpen={() => open({ trader: r.trader, metric: 'stale_setups' })} active={drill?.trader.id === r.trader.id && drill.metric === 'stale_setups'} />
                    </td>
                    <td className="px-3 py-2.5">
                      <Num n={r.unplannedTrades} tone="warn" label={`${r.trader.name}: ${r.unplannedTrades} unplanned trades`} onOpen={() => open({ trader: r.trader, metric: 'unplanned' })} active={drill?.trader.id === r.trader.id && drill.metric === 'unplanned'} />
                    </td>
                    <td className="px-3 py-2.5">
                      {r.learning ? (
                        <button type="button" onClick={() => open({ trader: r.trader, metric: 'learning' })} aria-label={`${r.trader.name}: ${r.learning.completed} of ${r.learning.required} required items done`} className={cx('cursor-pointer rounded-md px-1.5 py-1 text-left text-sm', r.learning.overdue ? 'bg-down-bg text-down' : r.learning.completed === r.learning.required ? 'text-up' : 'text-ink2')}>
                          <span className="font-mono font-semibold">
                            {r.learning.completed}/{r.learning.required}
                          </span>{' '}
                          done{r.learning.overdue ? ` · ${r.learning.overdue} overdue` : ''}
                        </button>
                      ) : (
                        <span className="text-muted" title="Nothing assigned">
                          —
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {board.isLoading && <Empty>Loading…</Empty>}
          {!ready && <Empty>Pick a start and an end date, then press Show.</Empty>}
          {b && !b.rows.length && <Empty>No traders yet.</Empty>}
        </div>
      </Card>

      <div ref={drillRef}>
        {drill && (
          <Card tone="accent">
            <CardHeader
              title={
                <>
                  {drill.trader.name} · {METRIC_LABEL[drill.metric]}
                  {drill.outcome && <span className="font-normal text-muted"> · {OUTCOME_LABEL[drill.outcome]}</span>}
                </>
              }
              right={
                <Button variant="ghost" onClick={() => setDrill(null)}>
                  Close
                </Button>
              }
            />
            <ErrorText error={items.error ? errorText(items.error) : null} />
            {items.isLoading && <Empty>Loading…</Empty>}
            {items.data && !items.data.items.length && <Empty>Nothing here.</Empty>}
            <ul className="m-0 flex list-none flex-col p-0">
              {items.data?.items.map((i) => (
                <li key={i.id} className="flex flex-wrap items-center justify-between gap-3 border-t border-[#1a1f27] px-4 py-3 first:border-t-0">
                  <div className="flex min-w-[220px] flex-col gap-0.5">
                    <span className="font-semibold">{i.title}</span>
                    <span className="text-sm text-sub">{i.detail}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    {i.outcome && <Pill tone={TONE[i.outcome]}>{OUTCOME_LABEL[i.outcome]}</Pill>}
                    <span className="text-sm text-muted">
                      {drill.metric === 'unplanned' ? 'opened' : 'due'} {fmtTime(i.at, tz, true)}
                      {i.completedAt && drill.metric !== 'unplanned' ? ` · done ${fmtTime(i.completedAt, tz, true)}` : ''}
                    </span>
                    {i.link ? (
                      <Link to={i.link} className="font-semibold">
                        Open →
                      </Link>
                    ) : (
                      <span className="text-xs text-muted">nothing to open</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </>
  );
}
